import React, {useEffect, useRef, useState} from "react";
import "./AddComment.scss";
import "../embedded-gif-picker.css";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "quill-mention/autoregister";
import {convertMarkdownToHtml, convertToMarkdown, getMentionConfig} from "../helpers/helper";
import {FaGrin, FaImage, FaLink, FaPaperPlane, FaSpinner, FaTimes} from "react-icons/fa";
import GiphySearchBox from "react-giphy-searchbox";
import EmojiPicker from "emoji-picker-react";
import {usePost} from "../../hooks/usePost";
import {fetchUsers} from "../../utils/userSuggest";
import LinkModal from "../Components/LinkModal/LinkModal";

const Delta = Quill.import('delta');

const AddComments = ({
  commentText, 
  addComment, 
  onChange, 
  postId, 
  showEditor = false, 
  onInputFocus, 
  onCancel, 
  isEdit = false,
  initialText = "",
  initialImage = null,
  initialGif = null,
  onSaveEdit,
  disableMediaEditing = false,
}) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkModalData, setLinkModalData] = useState({
    url: "",
    text: "",
    isEdit: false,
    selection: null
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedGif, setSelectedGif] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Loading state
  // Track active formatting states
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false
  });
  const [isQuillReady, setIsQuillReady] = useState(false);
  const [currentEditorContent, setCurrentEditorContent] = useState("");
  const editorRef = useRef(null);
  const localQuillRef = useRef(null);
  const inputRef = useRef(null);
  const { handleImageUploads } = usePost();
  const initializingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const currentMentionListRef = useRef([]);
  const lastEmptySearchRef = useRef("");

  function selectionHasMention(quill) {
    const range = quill.getSelection();
    if (!range) return false;

    const length = range.length || 1;
    const contents = quill.getContents(range.index, length);

    return contents.ops?.some(
        op => typeof op.insert === "object" && op.insert.mention
    );
  }


  // Update the ref whenever onChange changes
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Initialize images and gif in edit mode (only if media editing is enabled)
  useEffect(() => {
    if (isEdit && !disableMediaEditing) {
      if (initialImage) {
        setUploadedImages([{ url: initialImage.url, file: null }]);
      }
      if (initialGif) {
        setSelectedGif(initialGif);
      }
    }
  }, [isEdit, initialImage, initialGif, disableMediaEditing]);

  // Reset quill ready state when showEditor changes
  useEffect(() => {
    if (!showEditor) {
      setIsQuillReady(false);
      setCurrentEditorContent("");
      initializingRef.current = false;
      // Only clear quill ref when hiding editor
      if (localQuillRef.current) {
        localQuillRef.current = null;
      }
    }
  }, [showEditor]);

  // Initialize Quill editor when showEditor becomes true
  useEffect(() => {
    if (!showEditor || !editorRef.current) {
      return;
    }

    // Check if already initialized and quill ref exists
    const existingEditor = editorRef.current.querySelector(".ql-editor");
    if (existingEditor && localQuillRef.current) {
      setIsQuillReady(true);
      return;
    }

    // Prevent double initialization
    if (initializingRef.current) {
      return;
    }

    initializingRef.current = true;

    // Clean up any existing toolbar
    const existingToolbar = editorRef.current.parentNode?.querySelector('.custom-quill-toolbar');
    if (existingToolbar) {
      existingToolbar.remove();
    }

    // Clear the editor content before initializing
    editorRef.current.innerHTML = '';

    const editorId = `comment-editor-${postId || "main"}-${Date.now()}`;
    editorRef.current.id = editorId;

    // Hide Quill's default toolbar
    const toolbarContainer = document.createElement("div");
    toolbarContainer.className = "custom-quill-toolbar";
    toolbarContainer.style.display = "none"; // Hide default toolbar
    toolbarContainer.innerHTML = `
      <span class="ql-formats">
        <button class="ql-bold"></button>
        <button class="ql-italic"></button>
        <button class="ql-underline"></button>
        <button class="ql-strike"></button>
      </span>
      <span class="ql-formats">
        <button class="ql-link"></button>
        <button class="ql-image"></button>
        <button class="ql-gif" type="button">GIF</button>
      </span>
      <span class="ql-formats">
        <button class="ql-clean"></button>
      </span>
    `;

    editorRef.current.parentNode.insertBefore(toolbarContainer, editorRef.current);

    const quill = new Quill(editorRef.current, {
      modules: {
        keyboard: {
          bindings: {
            bold: {
              key: "b",
              shortKey: true,
              handler(range) {
                if (selectionHasMention(this.quill, range)) {
                  // allow formatting but strip from mentions later
                  return true;
                }
                this.quill.format("bold", !this.quill.getFormat(range).bold);
                return false;
              }
            },
            italic: {
              key: "i",
              shortKey: true,
              handler(range) {
                if (selectionHasMention(this.quill, range)) {
                  return true;
                }
                this.quill.format("italic", !this.quill.getFormat(range).italic);
                return false;
              }
            },
            underline: {
              key: "u",
              shortKey: true,
              handler(range) {
                if (selectionHasMention(this.quill, range)) {
                  return true;
                }
                this.quill.format("underline", !this.quill.getFormat(range).underline);
                return false;
              }
            }
          }
        },
        toolbar: {
          container: toolbarContainer,
          handlers: {
            image: () => {
              // Open file input for image upload
              const input = document.createElement("input");
              input.setAttribute("type", "file");
              input.setAttribute("accept", "image/*");
              input.click();
              input.onchange = () => {
                const file = input.files?.[0];
                if (file) {
                  const url = URL.createObjectURL(file);
                  setUploadedImages([{file: file, url}]);
                }
              };
            },
            emoji: () => {
              setShowEmojiPicker(true);
            },

            gif: () => {
              setShowGifPicker(true);
            }
          }
        },
        mention: getMentionConfig({ currentMentionListRef, fetchUsers, lastEmptySearchRef }),
      },
      placeholder: isEdit ? "Edit your comment..." : "Add a comment...",
      theme: "snow"
    });

    // CRITICAL: Set the ref immediately after creation
    localQuillRef.current = quill;

    // 🔒 BLOCK formatting when cursor is on a mention
    const toolbar = localQuillRef.current.getModule("toolbar");

    const blockedFormats = ["bold", "italic", "underline", "strike", "link"];

    blockedFormats.forEach((format) => {
      toolbar.addHandler(format, () => {
        if (selectionHasMention(localQuillRef.current)) {
          return; // 🚫 do nothing
        }

        const current = localQuillRef.current.getFormat()[format];
        localQuillRef.current.format(format, !current);
      });
    });

    // STEP 3: Add comprehensive image blocking right after setting the ref
    // This must come BEFORE any other event listeners

    // Block 1: Intercept paste at browser level - only block direct image files
    quill.root.addEventListener('paste', (e) => {
      const clipboardData = e.clipboardData || window.clipboardData;

      if (!clipboardData) return;

      const items = clipboardData.items;
      let hasImageFile = false;

      // Check if clipboard contains actual image files (not HTML img tags)
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf('image') !== -1 && items[i].kind === 'file') {
            hasImageFile = true;
            break;
          }
        }
      }

      if (hasImageFile) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        alert('Please use the image upload button below to add images to your comment.');
        return false;
      }
    }, true); // Use capture phase

    /**
     * Adding editor-change event to handle placholder visibility and active formats when mentioned someone
     */
    quill.on('editor-change', () => {
      const root = quill.root;
      const contents = quill.getContents();
      const hasMention = contents.ops.some(op =>
          op.insert && typeof op.insert === 'object' && op.insert.mention
      );
      const hasText = quill.getText().trim().length > 0;

      if (hasText || hasMention) {
        root.classList.remove('ql-blank');
      } else {
        root.classList.add('ql-blank');
      }
    });

    // Block 3: Monitor text changes and remove any images that slip through
    quill.on('text-change', (delta, oldDelta, source) => {
      if (source === 'user') {
        // Check if any images were inserted
        const currentContents = quill.getContents();
        console.log(currentContents,"contents newwww");
        let hasImage = false;
        let imageIndex = -1;

        currentContents.ops.forEach((op, index) => {
          if (op.insert && typeof op.insert === 'object' && op.insert.image) {
            hasImage = true;
            imageIndex = index;
          }
        });

        if (hasImage) {
          // Remove the image
          const newOps = currentContents.ops.filter(op => {
            return !(op.insert && typeof op.insert === 'object' && op.insert.image);
          });

          quill.setContents(new Delta(newOps), 'silent');

          alert('Please use the image upload button (📷) below to add images to your comment.');
        }
      }

      // Continue with your existing text-change logic
      const text = quill.root.innerHTML;
      const markdown = convertToMarkdown(text);
      onChangeRef.current?.({target: {value: markdown}});
      setCurrentEditorContent(markdown.trim());

      // Update placeholder visibility
      const hasContent = quill.getText().trim().length > 0;
      const hasMention = quill.getContents().ops.some(op =>
          op.insert && typeof op.insert === 'object' && op.insert.mention
      );
      if (hasContent || hasMention) {
        quill.root.classList.remove('ql-blank');
      } else {
        quill.root.classList.add('ql-blank');
      }

      // Update active formats
      const range = quill.getSelection();
      if (range) {
        let formats;
        if (range.length === 0) {
          formats = quill.getFormat(range.index, 1);
        } else {
          formats = quill.getFormat(range.index, range.length);
        }
        setActiveFormats({
          bold: !!formats.bold,
          italic: !!formats.italic,
          underline: !!formats.underline,
          strike: !!formats.strike
        });
      }
    });

    /**
     * Setting initial content/Edited comment content in Quill must be done after the editor is fully initialized.
     * Using requestAnimationFrame ensures this runs in the next frame, after Quill setup is complete.
     */
    requestAnimationFrame(() => {
      let contentToSet = '';
      if (isEdit && initialText) {
        contentToSet = initialText;
      } else if (commentText) {
        contentToSet = commentText;
      }

      if (contentToSet && quill && localQuillRef.current) {
        try {
          const updatedContent = convertMarkdownToHtml(contentToSet);
          console.log("Converted initial content to HTML:", updatedContent);
          quill.clipboard.dangerouslyPasteHTML(updatedContent);
          const markdown = convertToMarkdown(quill.root.innerHTML);
          onChangeRef.current?.({target: {value: markdown}});
          setCurrentEditorContent(markdown.trim());
          // Remove ql-blank class if content exists
          const hasContent = quill.getText().trim().length > 0;
          if (hasContent) {
            quill.root.classList.remove('ql-blank');
          }
        } catch (err) {
          console.error("Error setting initial content:", err);
        }
      }

      const length = quill.getLength();
      quill.setSelection(length, length);

      if (!isEdit) {
        quill.focus();
      }

      setIsQuillReady(true);
      initializingRef.current = false;

      // Add click event listener to update formats when clicking in the editor
      if (quill.root) {
        const handleEditorClick = () => {
          setTimeout(() => {
            const range = quill.getSelection();
            if (range) {
              let formats;
              if (range.length === 0) {
                // Get format at cursor position
                formats = quill.getFormat(range.index, 1);
              } else {
                // Get format of selection
                formats = quill.getFormat(range.index, range.length);
              }
              setActiveFormats({
                bold: !!formats.bold,
                italic: !!formats.italic,
                underline: !!formats.underline,
                strike: !!formats.strike
              });
            }
          }, 10);
        };

        quill.root.addEventListener('click', handleEditorClick);
        quill.root.addEventListener('mouseup', handleEditorClick);
        quill.root.addEventListener('keyup', handleEditorClick);
      }
    });

    // Cleanup function for this specific initialization
    return () => {
      initializingRef.current = false;
    };
  }, [showEditor, postId, isEdit, commentText, initialText, disableMediaEditing]);

  const handleRemoveImage = (idx) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveGif = () => setSelectedGif(null);

  const handleGifSelect = (item) => {
    setSelectedGif({ url: item.images.fixed_height.url, id: item.id });
    setShowGifPicker(false);
  };

  const handleEmojiSelect = (emojiData, event) => {
    const quill = localQuillRef.current;
    if (quill) {
      const selection = quill.getSelection();
      const index = selection ? selection.index : quill.getLength();
      quill.insertText(index, emojiData.emoji);
      quill.setSelection(index + emojiData.emoji.length);
    }
    setShowEmojiPicker(false);
  };
  const handleKeyDown = async (e) => {
    const quill = localQuillRef.current;
    const mentionModule = quill.getModule('mention');
    if (
        mentionModule &&
        mentionModule.isOpen &&
        e.key === ' ' &&
        currentMentionListRef.current.length === 1
    ) {
      e.preventDefault();
      mentionModule.selectItem(currentMentionListRef.current[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentContent = isEdit ? currentEditorContent : (commentText && commentText.trim());
    const hasContent = disableMediaEditing 
      ? currentContent 
      : (currentContent || (uploadedImages?.length > 0 || selectedGif));

    if (hasContent) {
      setIsLoading(true);
      let media = {}
      try {
        // Handle GIF - only upload if it's a new GIF and media editing is enabled
        if (!disableMediaEditing && selectedGif) {
          const isExistingGif = isEdit && initialGif && selectedGif.url === initialGif.url;
          
          if (isExistingGif) {
            // Use existing GIF data
            media.gif = {
              url: initialGif.url,
              id: initialGif.id
            };
          } else {
            // Upload new GIF
            const response = await handleImageUploads(selectedGif?.url, true);
            if (response.data.data[0]?.files[0]) {
              media.gif = {
                url: response.data.data[0]?.files[0]?.path,
                height: await new Promise(resolve => {
                  const img = new window.Image();
                  img.onload = () => resolve(img.height);
                  img.src = selectedGif.url;
                }),
                width: await new Promise(resolve => {
                  const img = new window.Image();
                  img.onload = () => resolve(img.width);
                  img.src = selectedGif.url;
                }),
                id: selectedGif.id
              };
            }
          }
        } else if (isEdit && initialGif && !disableMediaEditing) {
          // GIF was removed in edit mode - explicitly set to null (only if media editing enabled)
          media.gif = null;
        }

        // Handle Image - only upload if it's a new image file and media editing is enabled
        if (!disableMediaEditing && uploadedImages?.length > 0) {
          const uploadedImage = uploadedImages[0];
          const isExistingImage = isEdit && initialImage && uploadedImage.url === initialImage.url && !uploadedImage.file;
          
          if (isExistingImage) {
            // Use existing image data
            media.image = {
              url: initialImage.url
            };
          } else if (uploadedImage.file) {
            // Upload new image file
            const response = await handleImageUploads([uploadedImage.file], false);
            if (response.data.data) {
              media.image = {
                url: response.data.data[0].files[0]?.path,
                height: await new Promise(resolve => {
                  const img = new window.Image();
                  img.onload = () => resolve(img.height);
                  img.src = response.data.data[0].files[0]?.path;
                }),
                width: await new Promise(resolve => {
                  const img = new window.Image();
                  img.onload = () => resolve(img.width);
                  img.src = response.data.data[0].files[0]?.path;
                }),
              }
            }
          }
        } else if (isEdit && initialImage && !disableMediaEditing) {
          // Image was removed in edit mode - explicitly set to null (only if media editing enabled)
          media.image = null;
        }

        if (isEdit) {
          // Get the current content from the Quill editor
          const currentContent = localQuillRef.current ? convertToMarkdown(localQuillRef.current.root.innerHTML) : commentText;
          await onSaveEdit({ ...media, text: currentContent });
        } else {
          await addComment(media);
        }

        // Clear the editor
        if (localQuillRef.current && localQuillRef.current.root) {
          localQuillRef.current.root.innerHTML = "";
        }

        // Reset media
        setUploadedImages([]);
        setSelectedGif(null);
      } catch (error) {
        console.error("Error submitting comment:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  /**
   * Handle mention button click to open mention menu, if Quill is ready
   */
  const handleMention = () => {
    const quill = localQuillRef.current;
    if (quill && isQuillReady) {
      quill.getModule("mention").openMenu("@");
    }
  }

  const getFormatHandler = (formatType) => {
    return (e) => {
      e.preventDefault();
      e.stopPropagation();
            
      const quill = localQuillRef.current;
      if (!quill) {
        console.error("Quill instance not found!");
        return;
      }
      
      if (!isQuillReady) {
        console.warn("Quill not ready yet");
        return;
      }

      // Block formatting if selection has a mention
      if (selectionHasMention(quill)) {
        return;
      }
      
      // Get current selection
      let range = quill.getSelection();
      
      // If no selection, focus and try again
      if (!range) {
        quill.focus();
        range = quill.getSelection();
        if (!range) {
          console.error("Still no selection after focus");
          return;
        }
      }

      // Get current format
      const format = quill.getFormat(range);
      const currentValue = format[formatType];
      const newValue = !currentValue;
      
      
      // Apply format
      quill.format(formatType, newValue);
      
      // Update state
      setActiveFormats(prev => ({
        ...prev,
        [formatType]: newValue
      }));
      
      // Refocus to keep cursor in editor
      quill.focus();
    };
  };

  const handleLink = () => {
    const quill = localQuillRef.current;
    if (!quill || !isQuillReady) return;

    const selection = quill.getSelection();
    if (!selection) {
      quill.focus();
      return;
    }

    const format = quill.getFormat(selection.index, selection?.length);
    const selectedText = quill.getText(selection.index, selection?.length);

    setLinkModalData({
      url: format.link || "",
      text: selectedText,
      isEdit: !!format.link,
      selection
    });

    setShowLinkModal(true);
  };

  const handleLinkSave = ({ url, text }) => {
    const quill = localQuillRef.current;
    const { selection } = linkModalData;

    if (!quill || !selection) return;

    if (url === null) {
      quill.format("link", false);
    } else {
      if (selection?.length > 0) {
        quill.deleteText(selection.index, selection?.length);
        quill.insertText(selection.index, text, "link", url);
      } else {
        quill.insertText(selection.index, text, "link", url);
      }
    }

    quill.focus();
  };

  // Handle clicks outside the pickers to close them
  useEffect(() => {
    if (!showGifPicker && !showEmojiPicker) return;

    const handleClickOutside = (event) => {
      const gifPickerElement = document.querySelector('.gif-picker-embedded');
      const emojiPickerElement = document.querySelector('.emoji-picker-embedded');
      
      // Check if click is on picker buttons
      const isClickedOnGifButton = event.target.closest('.toolbar-btn') &&
        event.target.closest('.toolbar-btn').title === 'Add GIF';
      const isClickedOnEmojiButton = event.target.closest('.toolbar-btn') &&
        event.target.closest('.toolbar-btn').title === 'Add Emoji';
      
      // Close GIF picker if click is outside and not on GIF button
      if (showGifPicker && gifPickerElement &&
        !gifPickerElement.contains(event.target) &&
        !isClickedOnGifButton) {
        setShowGifPicker(false);
      }
      
      // Close emoji picker if click is outside and not on emoji button
      if (showEmojiPicker && emojiPickerElement &&
        !emojiPickerElement.contains(event.target) &&
        !isClickedOnEmojiButton) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showGifPicker, showEmojiPicker]);

  return (
    <div className="add-comment-container">
      <form onSubmit={handleSubmit} className="comment-form">
        {!showEditor ? (
          <div className="simple-input-container">
            <input
              ref={inputRef}
              type="text"
              placeholder="Add a comment..."
              onFocus={onInputFocus}
              className="simple-comment-input"
              aria-label="Add a comment"
              value={commentText}
              onChange={e => {
                onChangeRef.current?.({ target: { value: e.target.value } });
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
        ) : (
          <div className="comment-input-container">
            <div className="comment-editor-wrapper">
              <div
                ref={editorRef}
                className="comment-editor"
                id={`comment-editor-container-${postId || "main"}`}
                onKeyDown={handleKeyDown}
              ></div>
            </div>
            {!disableMediaEditing && (uploadedImages?.length > 0 || selectedGif) && (
              <div className="media-preview-section">
                <div className="media-attachments">
                  <div className="media-preview-list">
                    {uploadedImages.map((img, idx) => (
                      <div className="media-preview-item" key={`image-${idx}`}>
                        <div className="media-preview-content">
                          <img src={img.url} alt="preview" className="media-preview-thumb" />
                        </div>
                        <button type="button" className="remove-media-btn" onClick={() => handleRemoveImage(idx)} aria-label="Remove image">
                          ×
                        </button>
                      </div>
                    ))}
                    {selectedGif && (
                      <div className="media-preview-item gif">
                        <div className="media-preview-content">
                          <img src={selectedGif.url} alt="gif" className="media-preview-thumb" />
                        </div>
                        <button type="button" className="remove-media-btn" onClick={handleRemoveGif} aria-label="Remove GIF">
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="comment-actions">
              <div className="toolbar-actions">
                <button
                  type="button" 
                  className={`toolbar-btn ${activeFormats.bold ? 'active' : ''}`}
                  onClick={getFormatHandler("bold")} 
                  title="Bold"
                  disabled={!isQuillReady}
                >
                  <strong>B</strong>
                </button>
                <button 
                  type="button" 
                  className={`toolbar-btn ${activeFormats.italic ? 'active' : ''}`}
                  onClick={getFormatHandler("italic")} 
                  title="Italic"
                  disabled={!isQuillReady}
                >
                  <em>I</em>
                </button>
                <button 
                  type="button" 
                  className={`toolbar-btn ${activeFormats.underline ? 'active' : ''}`}
                  onClick={getFormatHandler("underline")} 
                  title="Underline"
                  disabled={!isQuillReady}
                >
                  <u>U</u>
                </button>
                {/*<button */}
                {/*  type="button" */}
                {/*  className={`toolbar-btn ${activeFormats.strike ? 'active' : ''}`}*/}
                {/*  onClick={getFormatHandler("strike")} */}
                {/*  title="Strikethrough"*/}
                {/*  disabled={!isQuillReady}*/}
                {/*>*/}
                {/*  <s>S</s>*/}
                {/*</button>*/}
                <button
                    type="button"
                    className={`toolbar-btn`}
                    onClick={()=>{handleMention()}}
                    disabled={!isQuillReady}
                    title="Mention User"
                >
                  @
                </button>
                <button 
                  type="button" 
                  className="toolbar-btn" 
                  onClick={handleLink} 
                  title="Link"
                  disabled={!isQuillReady}
                >
                  <FaLink />
                </button>
                <button type="button" className={`toolbar-btn ${showEmojiPicker ? 'active' : ''}`} onClick={() => {
                  setShowGifPicker(false);
                  setShowEmojiPicker(prev => !prev);
                }} title="Add Emoji">
                  <FaGrin />
                </button>
                {!disableMediaEditing && (
                  <>
                    <button type="button" className={`toolbar-btn ${showGifPicker ? 'active' : ''}`} onClick={() => {
                  setShowEmojiPicker(false);
                  setShowGifPicker(prev => !prev);
                }} title="Add GIF">
                  <span style={{ fontWeight: 'bold', fontSize: '12px' }}>GIF</span>
                </button>
                    <button type="button" className="toolbar-btn" onClick={() => {
                      const input = document.createElement("input");
                      input.setAttribute("type", "file");
                      input.setAttribute("accept", "image/*");
                      input.click();
                      input.onchange = () => {
                        const file = input.files?.[0];
                        if (file) {
                          const url = URL.createObjectURL(file);
                          setUploadedImages([{ file, url }]);
                        }
                      };
                    }} title="Add Image">
                      <FaImage />
                    </button>
                  </>
                )}
              </div>
              <div className="action-buttons">
                <button type="button" className="cancel-button" onClick={handleCancel} aria-label="Cancel" disabled={isLoading}>
                  <FaTimes style={{display:"flex"}}/>
                </button>
                <button
                  type="submit"
                  className={`submit-button ${(() => {
                    const currentContent = isEdit ? currentEditorContent : (commentText && commentText.trim());
                    return disableMediaEditing 
                      ? (!currentContent ? "disabled" : "")
                      : (!currentContent && uploadedImages?.length === 0 && !selectedGif ? "disabled" : "");
                  })()} ${isLoading ? "loading" : ""}`}
                  disabled={isLoading || (() => {
                    const currentContent = isEdit ? currentEditorContent : (commentText && commentText.trim());
                    return disableMediaEditing 
                      ? !currentContent
                      : (!currentContent && uploadedImages?.length === 0 && !selectedGif);
                  })()}
                  aria-label={isLoading ? "Submitting comment..." : "Submit comment"}
                >
                  {isLoading ? <FaSpinner className="spinner" /> : <FaPaperPlane />}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>

      <LinkModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onSave={handleLinkSave}
        initialUrl={linkModalData.url}
        initialText={linkModalData.text}
        isEdit={linkModalData.isEdit}
      />

      {/* Emoji picker embedded directly below toolbar */}
      {showEmojiPicker && (
        <div className="emoji-picker-embedded">
          <div className="emoji-picker-header">
            <span className="emoji-picker-title">Select an Emoji</span>
            <button
              className="close-emoji-btn"
              onClick={() => setShowEmojiPicker(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="emoji-picker-content">
            <EmojiPicker
              onEmojiClick={handleEmojiSelect}
              autoFocusSearch={false}
              theme="light"
              width="100%"
              height={350}
              searchPlaceholder="Search emojis..."
              previewConfig={{
                showPreview: false
              }}
            />
          </div>
        </div>
      )}

      {!disableMediaEditing && showGifPicker && (
        <div className="gif-picker-embedded">
          <div className="gif-picker-header">
            <span className="gif-picker-title">Select a GIF</span>
            <button
              className="close-gif-btn"
              onClick={() => setShowGifPicker(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="gif-picker-content">
            <GiphySearchBox
              apiKey="iRGwQCcO6qVRxtl6Svwg0KOHoHVX780w"
              onSelect={handleGifSelect}
              searchPlaceholder="Search GIF's"
              poweredByGiphy={true}
              rating="g"
              autoFocus={true}
              masonryConfig={[
                { columns: 3, imageWidth: 110, gutter: 8 },
                { mq: "500px", columns: 3, imageWidth: 110, gutter: 8 },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddComments;